import { LE } from "@/constants/enums";
import type { HasMurn, Lookup } from "@modernary/mui/components/lookup";
import type { PagingQuery } from "@modernary/mui/utils/api";
import type { EntityWithAuthRequestFields } from "@modernary/mui/utils/helpers";
import { ChangeOfOperatorApplication } from "./formTagsAndMappings/changeOfOperator";
import { DrainageInjectionWellPermitRenewalApplication } from "./formTagsAndMappings/drainageInjectionWellPermitRenewal";
import type { ParsedFormSubmission } from "./parsedFormSubmission";
import type { FormKitGroupValue } from "@formkit/core";
import { ExistingDrainageInjectionWell } from "./formTagsAndMappings/existingDrainageInjectionWell";
import { GeneralOperating } from "./formTagsAndMappings/generalOperating";
import { InjectionWellAbandonment } from "./formTagsAndMappings/injectionWellAbandonment";
import { DrainageInjectionWellGeneral } from "./formTagsAndMappings/drainageInjectionWellGeneral";
import { InjectionWellRenewal } from "./formTagsAndMappings/injectionWellRenewal";
import { ExistingInjectionWell } from "./formTagsAndMappings/existingInjectionWell";
import { FacilityNameChange } from "./formTagsAndMappings/facilityNameChange";

export interface FormSubmission extends EntityWithAuthRequestFields, HasMurn<"status">, HasMurn<"assignedTo"> {
  href: string;
  id: number;
  submissionNumber: string;
  revisionNumber: number;
  submitterName: string;
  //submissionDateTime: string;
  submissionOriginalDateTime: string;
  submissionLatestDateTime: string;
  formName?: Lookup;
  formNameMurn: LE.FormName;
  receivedMessageId: number;
  createdDateTime: string;
  formSubmissionJson: string;
  parsedFormSubmission?: ParsedFormSubmission;
  referenceNumber: string;
  _drainageInjectionWellPermitRenewalApplication?: DrainageInjectionWellPermitRenewalApplication.Application;
  _changeOfOperatorApplication?: ChangeOfOperatorApplication.Application;
  _existingDrainageInjectionWellApplication?: ExistingDrainageInjectionWell.Application;
  _generalOperatingApplication?: GeneralOperating.Application;
  _injectionWellAbandonmentApplication?: InjectionWellAbandonment.Application;
  _drainageInjectionWellGeneralApplication?: DrainageInjectionWellGeneral.Application;
  _injectionWellRenewalApplication?: InjectionWellRenewal.Application;
  _existingInjectionWellApplication?: ExistingInjectionWell.Application;
  _facilityNameChangeApplication?: FacilityNameChange.Application;
}

export interface FormSubmissionQuery extends PagingQuery {}

export interface FormSubmissionFormKit extends FormSubmission, FormKitGroupValue {}

export interface FormSubmissionAddress {
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export interface FormSubmissionCoordinates {
  latitude: string;
  longitude: string;
}

export interface FormSubmissionPerson {
  fullName: string;
  title: string;
}
export interface FormSubmissionSelect {
  description: string;
  other?: string;
}

export interface FormSubmissionContact extends FormSubmissionAddress {
  firstName?: string;
  lastName?: string;
  company?: string;
  title?: string;
  prefix?: string;
  email?: string;
  fax?: string;
  phones?: {
    number?: string;
    extension?: string;
    type?: string;
  }[];
}

//Map that indicates which form belongs to which form mapping based on its formNameMurns
export const formDetailsMapping = new Map<
  LE.FormName,
  | DrainageInjectionWellPermitRenewalApplication.FormSectionMapping
  | ChangeOfOperatorApplication.FormSectionMapping
  | ExistingDrainageInjectionWell.FormSectionMapping
  | GeneralOperating.FormSectionMapping
  | InjectionWellAbandonment.FormSectionMapping
  | DrainageInjectionWellGeneral.FormSectionMapping
  | InjectionWellRenewal.FormSectionMapping
  | ExistingInjectionWell.FormSectionMapping
  | FacilityNameChange.FormSectionMapping
>([
  [
    LE.FormName.DrainageInjectionWellRenewal,
    DrainageInjectionWellPermitRenewalApplication.DrainageInjectionWellPermitRenewalMapping,
  ],
  [LE.FormName.ChangeOfOperator, ChangeOfOperatorApplication.ChangeOfOperatorMapping],
  [LE.FormName.ExistingDrainageInjectionWell, ExistingDrainageInjectionWell.ExistingDrainageInjectionWellMapping],
  [LE.FormName.GeneralOperating, GeneralOperating.GeneralOperatingMapping],
  [LE.FormName.InjectionWellAbandonment, InjectionWellAbandonment.InjectionWellAbandonmentMapping],
  [LE.FormName.DrainageInjectionWellGeneral, DrainageInjectionWellGeneral.DrainageInjectionWellGeneralMapping],
  [LE.FormName.InjectionWellRenewal, InjectionWellRenewal.InjectionWellRenewalMapping],
  [LE.FormName.ExistingInjectionWell, ExistingInjectionWell.ExistingInjectionWellMapping],
  [LE.FormName.FacilityNameChange, FacilityNameChange.FacilityNameChangeMapping],
]);
